import type { ArtifactContent } from "../ChatEventTypes";
import type { MentionData } from "../components/Mention";
import { artifactVideoSourceMentions } from "../utils/videoSourceMentions";

/**
 * Raw `video_mention` event content, as hydrated server-side by
 * VideoMentionStreamEventProcessor.
 */
export type VideoMentionContent = {
	id?: string;
	video_id?: string;
	start_time_sec?: number | null;
	end_time_sec?: number | null;
} & Record<string, unknown>;

export type CollectedMentions = {
	social_account_mention: MentionData[];
	influencer_mention: MentionData[];
	video_mention: VideoMentionContent[];
};

/** Identity of a mentioned account: its public id, else handle within platform. */
const accountKey = (mention: MentionData): string =>
	mention.id ?? `${mention.platform ?? ""}:${mention.handle}`.toLowerCase();

/**
 * Identity of a mentioned clip: source video plus time range, so that two
 * sections of one video stay distinct. `content.id` is a freshly generated
 * NanoID on every event (see VideoMentionStreamEventProcessor), so it only
 * serves as a fallback for a video that could not be resolved.
 */
const videoKey = (mention: VideoMentionContent): string => {
	const video = mention.video_id ?? mention.id ?? "";
	return `${video}:${mention.start_time_sec ?? ""}:${mention.end_time_sec ?? ""}`;
};

/**
 * Collects the mentions a conversation's stream events carry, so that a host app
 * can read the lot back at any point (see MobileChatInterface.getMentions).
 *
 * Every mention event is recorded, whether or not it was also dispatched to the
 * app and whether it arrived live or as replayed history. The clips behind a
 * `video_sources` artifact join the videos cited inline, folded onto the same
 * shape (see videoSourceMentions), so that `video_mention` holds every clip the
 * conversation has put in front of the user rather than only the cited ones.
 * Repeats — a source that was also cited, or a whole second replay of history
 * after `refreshConversation()` — collapse onto the keys above, first
 * occurrence winning.
 */
export function createMentionCollector() {
	const collected = {
		social_account_mention: new Map<string, MentionData>(),
		influencer_mention: new Map<string, MentionData>(),
		video_mention: new Map<string, VideoMentionContent>(),
	};

	const recordVideo = (mention: VideoMentionContent) => {
		const key = videoKey(mention);
		if (!collected.video_mention.has(key)) {
			collected.video_mention.set(key, mention);
		}
	};

	const record = (eventType: string, content: unknown) => {
		if (content == null) {
			return;
		}
		switch (eventType) {
			case "social_account_mention":
			case "influencer_mention": {
				const mention = content as MentionData;
				const key = accountKey(mention);
				if (!collected[eventType].has(key)) {
					collected[eventType].set(key, mention);
				}
				break;
			}
			case "video_mention": {
				recordVideo(content as VideoMentionContent);
				break;
			}
			case "artifact_created": {
				for (const source of artifactVideoSourceMentions(
					content as ArtifactContent,
				)) {
					recordVideo(source);
				}
				break;
			}
		}
	};

	/** Fresh arrays, so a caller cannot mutate what has been collected. */
	const getMentions = (): CollectedMentions => ({
		social_account_mention: [...collected.social_account_mention.values()],
		influencer_mention: [...collected.influencer_mention.values()],
		video_mention: [...collected.video_mention.values()],
	});

	return { record, getMentions };
}
